import { useEffect, useState, useCallback, useMemo } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Filters, Th, SortableTh, Td, IconBtn, Pagination } from "./AdminUsers";
import { ShieldCheck, Ban, RotateCcw, TrendingUp, Eye, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import UserDetailsDrawer from "@/components/admin/UserDetailsDrawer";
import { exportCSV, exportExcel } from "@/lib/exporters";

export default function AdminIssuers({ kind = "organizations" }) {
  const isOrg = kind === "organizations";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 25;
  const [sortKey, setSortKey] = useState("organization_name");
  const [sortDir, setSortDir] = useState("asc");
  const [drawerUid, setDrawerUid] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/${kind}`, { params: { q, status_filter: statusFilter, skip, limit } });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) {
      /* errors surfaced via toast at action sites */
    }
  }, [kind, q, statusFilter, skip]);
  useEffect(() => { load(); }, [load]);

  const handleSetQ = (val) => { setQ(val); setSkip(0); };
  const handleSetStatus = (val) => { setStatusFilter(val); setSkip(0); };

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const av = (a[sortKey] || a.full_name || "").toString().toLowerCase();
      const bv = (b[sortKey] || b.full_name || "").toString().toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const act = async (uid, payload) => {
    try {
      await api.post(`/admin/${kind}/${uid}/action`, payload);
      toast.success("Done");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const EXPORT_COLUMNS = [
    { key: "name", label: "Name", value: (u) => u.organization_name || u.full_name || "" },
    { key: "email", label: "Email" },
    { key: "external_id", label: isOrg ? "Org ID" : "Institution ID", value: (u) => u.org_id || u.institution_id || "" },
    { key: "members", label: isOrg ? "Employees" : "Students", value: (u) => (isOrg ? u.employee_count : u.student_count) || 0 },
    { key: "cert_count", label: "Certificates", value: (u) => u.cert_count || 0 },
    { key: "dispute_count", label: "Disputes", value: (u) => u.dispute_count || 0 },
    { key: "plan", label: "Plan", value: (u) => u.plan || "free" },
    { key: "verification_status", label: "Verification", value: (u) => (u.suspended ? "Suspended" : u.verification_status || "Unverified") },
  ];

  const fetchAllForExport = async () => {
    try {
      const { data } = await api.get(`/admin/${kind}`, { params: { q, status_filter: statusFilter, skip: 0, limit: 5000 } });
      return data.rows;
    } catch {
      toast.error("Failed to fetch export rows");
      return [];
    }
  };
  const handleExportCSV = async () => {
    const all = await fetchAllForExport();
    if (all.length === 0) return;
    exportCSV(`ethiccred-${kind}-${Date.now()}`, all, EXPORT_COLUMNS);
    toast.success(`Exported ${all.length} rows (CSV)`);
  };
  const handleExportExcel = async () => {
    const all = await fetchAllForExport();
    if (all.length === 0) return;
    exportExcel(`ethiccred-${kind}-${Date.now()}`, all, EXPORT_COLUMNS, isOrg ? "Organizations" : "Institutions");
    toast.success(`Exported ${all.length} rows (Excel)`);
  };

  return (
    <AdminLayout
      subtitle={isOrg ? "Module 3" : "Module 4"}
      title={isOrg ? "Organization Management" : "Institution Management"}
      actions={
        <div className="flex items-center gap-3">
          <div className="text-xs font-mono-plex text-zinc-500">{total.toLocaleString()} {isOrg ? "organizations" : "institutions"}</div>
          <Button size="sm" variant="outline" onClick={handleExportCSV} className="rounded-sm h-9 border-white/10 text-white hover:bg-white/10 gap-2" data-testid={`${kind}-export-csv`}>
            <Download className="w-3.5 h-3.5" />CSV
          </Button>
          <Button size="sm" variant="outline" onClick={handleExportExcel} className="rounded-sm h-9 border-white/10 text-white hover:bg-white/10 gap-2" data-testid={`${kind}-export-excel`}>
            <FileSpreadsheet className="w-3.5 h-3.5" />Excel
          </Button>
        </div>
      }
    >
      <Filters q={q} setQ={handleSetQ} statusFilter={statusFilter} setStatusFilter={handleSetStatus} testid={`${kind}-filters`} />

      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${kind}-table`}>
            <thead className="bg-white/5">
              <tr className="text-left">
                <SortableTh sortKey="organization_name" current={sortKey} dir={sortDir} onClick={toggleSort} testid={`${kind}-th-name`}>{isOrg ? "Organization" : "Institution"}</SortableTh>
                <SortableTh sortKey={isOrg ? "employee_count" : "student_count"} current={sortKey} dir={sortDir} onClick={toggleSort} testid={`${kind}-th-members`}>{isOrg ? "Employees" : "Students"}</SortableTh>
                <SortableTh sortKey="cert_count" current={sortKey} dir={sortDir} onClick={toggleSort} testid={`${kind}-th-certs`}>Certificates</SortableTh>
                <Th>Disputes</Th>
                <SortableTh sortKey="plan" current={sortKey} dir={sortDir} onClick={toggleSort} testid={`${kind}-th-plan`}>Plan</SortableTh>
                <Th>Verification</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((u) => (
                <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`${kind}-row-${u.id}`}>
                  <Td>
                    <div className="font-semibold text-white">{u.organization_name || u.full_name}</div>
                    <div className="text-xs text-zinc-500">{u.email}</div>
                    <div className="text-[10px] font-mono-plex text-zinc-600">{u.org_id || u.institution_id}</div>
                  </Td>
                  <Td className="font-mono-plex">{(isOrg ? u.employee_count : u.student_count) || 0}</Td>
                  <Td className="font-mono-plex">{u.cert_count || 0}</Td>
                  <Td>{u.dispute_count ? <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 rounded-sm">{u.dispute_count}</Badge> : <span className="text-zinc-600">—</span>}</Td>
                  <Td className="text-xs">{u.plan || "free"}</Td>
                  <Td>
                    {u.suspended ? (
                      <Badge className="bg-ec-error/20 text-ec-error border-ec-error/40 rounded-sm">Suspended</Badge>
                    ) : (u.verification_status && u.verification_status !== "Unverified") ? (
                      <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 rounded-sm gap-1"><ShieldCheck className="w-3 h-3" /> Verified</Badge>
                    ) : (
                      <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 rounded-sm">Pending</Badge>
                    )}
                  </Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <IconBtn title="View details" onClick={() => setDrawerUid(u.id)} testid={`${kind}-view-${u.id}`}>
                        <Eye className="w-3 h-3" />
                      </IconBtn>
                      {!u.suspended && (u.verification_status === "Unverified" || !u.verification_status) && (
                        <IconBtn title="Verify" onClick={() => act(u.id, { action: "verify" })} testid={`${kind}-verify-${u.id}`}>
                          <ShieldCheck className="w-3 h-3" />
                        </IconBtn>
                      )}
                      {u.suspended ? (
                        <IconBtn title="Restore" onClick={() => act(u.id, { action: "restore" })} testid={`${kind}-restore-${u.id}`}>
                          <RotateCcw className="w-3 h-3" />
                        </IconBtn>
                      ) : (
                        <IconBtn title="Suspend" onClick={() => {
                          const note = prompt("Reason for suspension:") || "";
                          act(u.id, { action: "suspend", note });
                        }} testid={`${kind}-suspend-${u.id}`}>
                          <Ban className="w-3 h-3" />
                        </IconBtn>
                      )}
                      <IconBtn title="Upgrade plan" onClick={() => {
                        const plan = prompt("Plan name (Employer / Institution / Enterprise):") || "";
                        if (plan) act(u.id, { action: "upgrade_plan", plan });
                      }} testid={`${kind}-upgrade-${u.id}`}>
                        <TrendingUp className="w-3 h-3" />
                      </IconBtn>
                    </div>
                  </Td>
                </tr>
              ))}
              {sortedRows.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-zinc-500" data-testid={`${kind}-empty`}>No {kind} found.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Pagination total={total} skip={skip} limit={limit} setSkip={setSkip} />

      {drawerUid && <UserDetailsDrawer userId={drawerUid} onClose={() => setDrawerUid(null)} />}
    </AdminLayout>
  );
}
